import { Link } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";

export const PageHero = ({ eyebrow, title, text, children }) => (
  <section className="bg-zinc-950 text-white grid-lines relative overflow-hidden">
    <div className="absolute right-0 top-0 h-full w-1/3 bg-[#dc2626]/10 [clip-path:polygon(30%_0,100%_0,100%_100%,0_100%)]" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
      <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight max-w-3xl" data-testid="page-title">{title}</h1>
      {text && <p className="mt-4 text-zinc-300 max-w-2xl text-sm sm:text-base">{text}</p>}
      {children}
    </div>
  </section>
);

export const Section = ({ eyebrow, title, text, children, className = "", action }) => (
  <section className={`max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 ${className}`}>
    {(title || eyebrow) && (
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div>
          {eyebrow && <span className="eyebrow">{eyebrow}</span>}
          {title && <h2 className="mt-2 text-2xl sm:text-3xl font-black tracking-tight">{title}</h2>}
          {text && <p className="mt-2 text-zinc-600 max-w-2xl text-sm sm:text-base">{text}</p>}
        </div>
        {action}
      </div>
    )}
    {children}
  </section>
);

export const Loading = () => (
  <div className="flex justify-center py-16 text-zinc-400" data-testid="loading"><Loader2 className="animate-spin" /></div>
);

export const Empty = ({ text, testid = "empty-state" }) => (
  <div className="border border-dashed border-zinc-300 p-10 text-center text-zinc-500 text-sm flex flex-col items-center gap-2" data-testid={testid}>
    <ShieldCheck className="text-zinc-300" size={32} />
    {text}
  </div>
);

export const Badge = ({ children, tone = "zinc", testid }) => {
  const t = { zinc: "bg-zinc-100 text-zinc-700", red: "bg-red-50 text-red-700 border-red-200", green: "bg-green-50 text-green-700 border-green-200", amber: "bg-amber-50 text-amber-800 border-amber-200", dark: "bg-zinc-950 text-white" }[tone];
  return <span data-testid={testid} className={`inline-flex items-center rounded-full border border-transparent px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${t}`}>{children}</span>;
};

export const Placeholder = ({ text }) => (
  <span className="text-zinc-400 italic">{text}</span>
);

export const Text = ({ value, fallback }) => {
  if (!value) return <Placeholder text={fallback} />;
  if (/^\[.*\]$/.test(value.trim())) return <Placeholder text={value} />;
  return value.split("\n").map((p, i) => <p key={i} className="mb-3 leading-relaxed text-zinc-700">{p}</p>);
};

export const CtaLink = ({ to, children, variant = "red", testid, className = "" }) => (
  <Link to={to} data-testid={testid} className={`${variant === "red" ? "btn-red" : variant === "dark" ? "btn-dark" : "btn-outline"} ${className}`}>{children}</Link>
);
